/**
 * The eleven steps of the create-strategy wizard (W12 Part B; «Insights» added
 * by W13 IS2).
 *
 * Labels, titles and subtitles are the approved canvas v3.3 copy — kept
 * verbatim from `.design/create-strategy-wizard/tools/gen-wizard-v3.mjs`
 * (`STEPS` / `TITLES` / `GUIDE[key].subtitle`); the «Insights» step follows the
 * same cadence (mock `.design/create-strategy-wizard/mockups/insights-step.html`,
 * designer invariant 23 updated to eleven steps). This module is the single
 * source of truth for step identity, order and guide keys; the dialog owns the
 * step BODIES and the rail captions.
 */

export type WizardStepKey =
  | 'analysis'
  | 'tasks'
  | 'insights'
  | 'insightVars'
  | 'projectVars'
  | 'proof'
  | 'details'
  | 'blockTypes'
  | 'library'
  | 'generation'
  | 'review';

export interface WizardStepDef {
  /** Stable id — also the selector suffix and the guide-key suffix. */
  key: WizardStepKey;
  /** Rail label (short). */
  label: string;
  /** Step header `h5`. */
  title: string;
  /** One line under the title. */
  subtitle: string;
  /**
   * Guide document opened by the step's ⓘ. The ten keys are registered in
   * `@modules/guides/anchors/guideAnchorRegistry` as
   * `STRATEGY_WIZARD_GUIDE_ANCHORS` (deliberately NOT part of `GUIDE_ANCHORS` —
   * that set is the support-flow launch ROUTE registry) and seeded on DEV by
   * `scripts/guides/seed-strategy-wizard-guides.mjs` (Task B8); the
   * key is what `GuideViewerPopover` queries the `guides` collection with, so
   * these literals must stay identical in all three places.
   */
  guideKey: `strategy-wizard-${string}`;
}

export const WIZARD_STEPS: readonly WizardStepDef[] = [
  {
    key: 'analysis',
    label: 'Insight analysis',
    title: 'Pin the insight analysis',
    subtitle: 'One analysis grounds the whole strategy.',
    guideKey: 'strategy-wizard-analysis',
  },
  {
    key: 'tasks',
    label: 'Tasks',
    title: 'Choose the tasks',
    subtitle: 'The goals your users come with — each selected task becomes a route of the funnel.',
    guideKey: 'strategy-wizard-tasks',
  },
  {
    // W13 IS2: the theses the run is grounded in. It sits directly after
    // «Tasks» because a thesis is scoped to the global package or to a task —
    // what this step offers is decided by the routes chosen one step earlier.
    key: 'insights',
    label: 'Insights',
    title: 'Choose the insight theses',
    subtitle: 'What the actors told you; every checked thesis is sent to the AI as grounding.',
    guideKey: 'strategy-wizard-insights',
  },
  {
    key: 'insightVars',
    label: 'Insight variables',
    title: 'Choose the insight-derived variables',
    subtitle: 'Questions the AI derived from the insights of the pinned analysis; accepted ones are preselected.',
    guideKey: 'strategy-wizard-insight-variables',
  },
  {
    key: 'projectVars',
    label: 'Project variables',
    title: 'Choose the project variables',
    subtitle: 'Everything the funnel may ask your users: global questions plus the ones specific to the selected goals.',
    guideKey: 'strategy-wizard-project-variables',
  },
  {
    key: 'proof',
    label: 'Social proof',
    title: 'Choose the social proof',
    subtitle: 'Reviews, ratings, study cases and big numbers the AI may cite.',
    guideKey: 'strategy-wizard-social-proof',
  },
  {
    key: 'details',
    label: 'Details & tone',
    title: 'Describe the strategy',
    subtitle: 'How the AI should sound and what it should aim at.',
    guideKey: 'strategy-wizard-details-tone',
  },
  {
    key: 'blockTypes',
    label: 'Block types',
    title: 'Allow block types',
    subtitle: 'The AI plans only with the types allowed here.',
    guideKey: 'strategy-wizard-block-types',
  },
  {
    key: 'library',
    label: 'Building blocks',
    title: 'Add examples from the block library',
    subtitle: 'Optional. Examples are adapted by the AI by default.',
    guideKey: 'strategy-wizard-library',
  },
  {
    key: 'generation',
    label: 'AI settings',
    title: 'Set how the AI runs',
    subtitle: 'Which model runs, and when each block starts.',
    guideKey: 'strategy-wizard-ai-settings',
  },
  {
    key: 'review',
    label: 'Review & create',
    title: 'Review and create',
    subtitle: 'Confirm the request, then create.',
    guideKey: 'strategy-wizard-review',
  },
] as const;

export const wizardStepIndex = (key: WizardStepKey): number =>
  WIZARD_STEPS.findIndex((step) => step.key === key);

export const wizardStepDef = (key: WizardStepKey): WizardStepDef =>
  WIZARD_STEPS[wizardStepIndex(key)];

/** Rail state of one step. `error` is reserved for the readiness blockers (B3+). */
export type WizardStepStatus = 'pending' | 'done' | 'error';

export interface WizardRailItem {
  key: WizardStepKey;
  label: string;
  /** Live one-line summary of the step's selection («3 of 3 routes»). */
  caption: string;
  status: WizardStepStatus;
}
